import { Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonContent, IonHeader, IonItem, IonLabel, IonList, IonMenu, IonToolbar } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { combineLatest, map, startWith } from 'rxjs';

import { COMPANY_MENU_ID } from '@ninety/mobile/platform/api';
import { appActions, CompanyUserListModel, CompanyUserSelectors } from '@ninety/mobile/platform/state';

import { CompanyAvatarComponent } from '../company-avatar/company-avatar.component';
import { SearchbarComponent } from '../searchbar/searchbar.component';
import { SideMenuToolbarComponent } from '../side-menu-toolbar/side-menu-toolbar.component';

@Component({
  selector: 'nty-company-selector-menu',
  imports: [
    ReactiveFormsModule,
    LetDirective,
    SideMenuToolbarComponent,
    CompanyAvatarComponent,
    SearchbarComponent,
    IonContent,
    IonHeader,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonToolbar,
  ],
  host: { class: 'nty-side-menu' },
  template: `
    <ion-menu [menuId]="companyMenuId" [contentId]="contentId" *ngrxLet="vm$ as vm">
      <ion-header>
        <nty-side-menu-toolbar />

        <ion-toolbar>
          <nty-searchbar placeholder="Search for a company..." [formControl]="filterControl" />
        </ion-toolbar>

        <hr />
      </ion-header>

      <ion-content>
        <ion-list>
          @for (c of vm.filteredCompanyUsers; track c.userId) {
            <ion-item button="true" detail="false" lines="none" (click)="switchCompany(c)">
              <nty-company-avatar slot="start" size="30px" [logoUrl]="c?.company?.logo?.url" />
              <ion-label>
                <span class="terra-body">{{ c.company.name }}</span>
              </ion-label>
            </ion-item>
          }
        </ion-list>
      </ion-content>
    </ion-menu>
  `,
  styles: `
    @use 'terra';

    ion-item::part(native) {
      padding-left: 0;
    }

    ion-menu {
      --background: #{terra.color(background, heavy)};
    }

    ion-header ion-toolbar:first-of-type {
      padding-top: 0;
      background: terra.color(background, heavy);
    }

    ion-header {
      background: terra.color(background, heavy);
    }

    ion-toolbar {
      --padding-top: 40px;
      --padding-end: 24px;
      --padding-bottom: 24px;
      --padding-start: 24px;

      --background: #{terra.color(background, heavy)};
      --border-color: transparent;
    }

    ion-content {
      --padding-top: 8px;
      --padding-start: 24px;
      --padding-end: 24px;
      --background: #{terra.color(background, heavy)};
    }

    ion-thumbnail {
      --border-radius: 100%;
      --size: 30px;
    }

    ion-list {
      background: terra.color(background, heavy);
    }

    ion-item {
      --padding-top: 4px;
      --padding-end: 3px;
      --padding-bottom: 4px;
      --padding-start: 3px;
      --border-radius: #{terra.radius(small)};
      --background: #{terra.color(background, heavy)};
    }

    ion-label {
      color: terra.color(text) !important;
    }

    hr {
      background: terra.color(border);
      margin: 8px 24px 0;
    }

    ion-menu::part(container) {
      @include terra.elevation(2, right);
    }
  `,
})
export class CompanySelectorMenuComponent {
  @Input({ required: true }) contentId!: string;

  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  public readonly companyMenuId = inject(COMPANY_MENU_ID);

  public readonly filterControl = new FormControl<string>('');

  private readonly filteredCompanyUsers$ = combineLatest([
    this.filterControl.valueChanges.pipe(startWith('')),
    this.store.select(CompanyUserSelectors.selectActive),
  ]).pipe(
    map(([searchQuery, items]) => this.filterList(searchQuery, items)),
    takeUntilDestroyed(this.destroyRef)
  );

  public readonly vm$ = {
    currentCompanyUser: this.store.select(CompanyUserSelectors.selectCurrentCompanyUser),
    filteredCompanyUsers: this.filteredCompanyUsers$,
  };

  switchCompany(companyUser: CompanyUserListModel) {
    this.store.dispatch(appActions.switchCompany({ companyUserId: companyUser.userId }));
  }

  filterList(searchQuery: string | null, items: CompanyUserListModel[]): CompanyUserListModel[] {
    if (!items?.length) return [];
    if (!searchQuery) return items;

    const query = searchQuery.toLowerCase();
    return items.filter(item => !!item.company?.name && item.company.name.toLowerCase().includes(query));
  }
}
